const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class LrcTheme {
  constructor() {
    this.prefetched = new Set();
    this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  }

  init() {
    this.setupSelectedEventPage();
    this.setupEventInclusions();
    this.setupReveals();
    this.setupLinkPrefetch();
    this.setupSoftTransitions();
    this.setupHeroViewportHeight();
    this.setupRegistrationForms();
    this.setupRegistrationWizards();
    this.setupRegistrationCampaigns();
    this.setupPolicyDialogs();
    this.setupPriorityMedia();
    this.setupHeroVideos();
    this.setupHeroScrollCards();
    this.setupEventRegistrationLinks();
    this.setupRegistrationHighlights();
    this.setupCityEventTabs();
    this.setupEventGalleries();
    this.setupGalleryDirectories();
    this.setupFaqToggles();
    this.setupCommunityDialogs();
    this.setupProductGalleries();
    this.setupProductForms();
  }

  setupSelectedEventPage() {
    const selectedEvent = new URLSearchParams(window.location.search).get('event');
    document.documentElement.classList.toggle('lrc-has-selected-event', Boolean(selectedEvent));

    const policyFooter = document.querySelector('[data-lrc-event-policy-footer]');
    const pageMain = document.querySelector('main');
    if (policyFooter && pageMain) pageMain.append(policyFooter);

    if (!selectedEvent) return;

    const registration = document.getElementById('event-registration');
    const supportingContent = document.querySelector('[data-lrc-event-supporting]');
    if (registration && supportingContent) {
      registration.insertAdjacentElement('afterend', supportingContent);
    }
  }

  setupEventInclusions() {
    const registration = document.getElementById('event-registration');
    const update = () => {
      const selectedTitle = registration?.querySelector('.lrcq-event-detail .lrcq-event-hero h1')?.textContent;
      document.querySelectorAll('[data-lrc-event-inclusion-groups]').forEach((group) => {
        const name = (selectedTitle || group.dataset.lrcDefaultEventName || '')
          .toLowerCase().replace(/[-\u2010-\u2015]/g, ' ').replace(/\s+/g, ' ').trim();
        const pedalBike = !name.includes('push bike') && name.includes('pedal bike');
        const pushInclusions = group.querySelector('[data-lrc-event-inclusions="push-bike"]');
        const pedalInclusions = group.querySelector('[data-lrc-event-inclusions="pedal-bike"]');
        if (pushInclusions) pushInclusions.hidden = pedalBike;
        if (pedalInclusions) pedalInclusions.hidden = !pedalBike;
      });
    };
    update();
    this.eventInclusionsObserver?.disconnect();
    if (registration) {
      this.eventInclusionsObserver = new MutationObserver(update);
      this.eventInclusionsObserver.observe(registration, { childList: true, subtree: true, characterData: true });
    }
  }

  setupEventRegistrationLinks(scope = document) {
    scope.querySelectorAll('a[href="#event-registration"]').forEach((link) => {
      if (link.dataset.lrcRegistrationLinkReady === 'true') return;
      link.dataset.lrcRegistrationLinkReady = 'true';

      link.addEventListener('click', (event) => {
        const registration = document.getElementById('event-registration');
        if (!registration) return;

        event.preventDefault();
        registration.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });

        const url = new URL(window.location.href);
        url.hash = 'event-registration';
        window.history.replaceState({}, '', url);
      });
    });
  }

  setupReveals() {
    const nodes = document.querySelectorAll('.lrc-reveal');
    if (!nodes.length || reduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    nodes.forEach((node) => observer.observe(node));
  }

  setupLinkPrefetch() {
    if (this.connection?.saveData || ['slow-2g', '2g'].includes(this.connection?.effectiveType)) return;

    const canPrefetch = (link) => {
      if (!link.href || link.target || link.hasAttribute('download')) return false;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return false;
      return /^\/(collections|products|pages|blogs|cart|search)?\/?/.test(url.pathname);
    };

    const prefetch = (href, as = 'document') => {
      if (this.prefetched.has(href)) return;
      this.prefetched.add(href);

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.as = as;
      link.fetchPriority = 'low';
      document.head.appendChild(link);
    };

    const schedule = (link) => {
      if (!canPrefetch(link)) return;
      window.requestIdleCallback ? requestIdleCallback(() => prefetch(link.href), { timeout: 1800 }) : setTimeout(() => prefetch(link.href), 250);
    };

    document.addEventListener('pointerover', (event) => {
      const link = event.target.closest('a[href]');
      if (link) schedule(link);
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            schedule(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '300px 0px' });

      document.querySelectorAll('.lrc-product-card a[href], .lrc-button[href]').forEach((link) => observer.observe(link));
    }
  }

  setupSoftTransitions() {
    if (!document.startViewTransition || reduceMotion.matches) return;

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest('a[href]');
      if (!link || link.target || link.hasAttribute('download')) return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash && url.pathname === location.pathname) return;
      if (!/^\/(collections|products|pages|blogs|cart|search)?\/?/.test(url.pathname)) return;

      document.documentElement.classList.add('lrc-transitioning');
      window.addEventListener('pagehide', () => document.documentElement.classList.remove('lrc-transitioning'), { once: true });
    }, { capture: true });
  }

  setupHeroViewportHeight() {
    const root = document.documentElement;

    const update = () => {
      const header = document.querySelector('#header-group') || document.querySelector('.header-section');
      const headerHeight = header?.getBoundingClientRect().height || 0;
      root.style.setProperty('--lrc-header-offset', `${Math.round(headerHeight)}px`);
    };

    update();

    if (this.heroViewportHeightReady) return;
    this.heroViewportHeightReady = true;

    let resizeFrame = null;
    const requestUpdate = () => {
      if (resizeFrame !== null) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        update();
      });
    };

    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    window.visualViewport?.addEventListener('resize', requestUpdate, { passive: true });

    const header = document.querySelector('#header-group') || document.querySelector('.header-section');
    if ('ResizeObserver' in window && header) {
      this.heroViewportObserver = new ResizeObserver(requestUpdate);
      this.heroViewportObserver.observe(header);
    }
  }

  setupRegistrationForms() {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-lrc-registration-form]');
      if (!form) return;

      const success = form.querySelector('[data-lrc-form-success]');
      if (!success) return;

      success.hidden = false;
      success.focus({ preventScroll: true });
    });
  }

  setupRegistrationWizards() {
    document.querySelectorAll('[data-lrc-registration-flow]').forEach((flow) => {
      const panels = [...flow.querySelectorAll('[data-lrc-registration-panel]')];
      const indicators = [...flow.querySelectorAll('[data-lrc-registration-indicator]')];
      if (!panels.length) return;

      let activeIndex = Math.max(0, panels.findIndex((panel) => panel.classList.contains('is-active')));

      const showPanel = (nextIndex, shouldFocus = true) => {
        activeIndex = Math.max(0, Math.min(nextIndex, panels.length - 1));

        panels.forEach((panel, index) => {
          const active = index === activeIndex;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });

        indicators.forEach((indicator, index) => {
          indicator.classList.toggle('is-active', index === activeIndex);
          indicator.classList.toggle('is-complete', index < activeIndex);
          indicator.setAttribute('aria-current', index === activeIndex ? 'step' : 'false');
        });

        if (shouldFocus) {
          panels[activeIndex].querySelector('legend')?.focus({ preventScroll: true });
          flow.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        }
      };

      const panelIsValid = (panel) => {
        const requiredFields = [...panel.querySelectorAll('[required]')];
        const radioGroups = new Set();

        for (const field of requiredFields) {
          if (field.type === 'radio') {
            if (radioGroups.has(field.name)) continue;
            radioGroups.add(field.name);
            const selected = panel.querySelector(`input[type="radio"][name="${CSS.escape(field.name)}"]:checked`);
            if (!selected) {
              field.reportValidity();
              return false;
            }
            continue;
          }

          if (!field.checkValidity()) {
            field.reportValidity();
            return false;
          }
        }

        return true;
      };

      flow.addEventListener('click', (event) => {
        const next = event.target.closest('[data-lrc-registration-next]');
        const back = event.target.closest('[data-lrc-registration-back]');
        if (!next && !back) return;

        if (next && panelIsValid(panels[activeIndex])) showPanel(activeIndex + 1);
        if (back) showPanel(activeIndex - 1);
      });

      showPanel(activeIndex, false);
    });
  }

  setupRegistrationCampaigns(scope = document) {
    scope.querySelectorAll('[data-lrc-registration-campaign]').forEach((campaign) => {
      if (campaign.dataset.registrationCampaignReady === 'true') return;

      const eventCards = [...campaign.querySelectorAll('[data-lrc-registration-event]')];
      const eventSelect = campaign.querySelector('[data-lrc-registration-event-select]');
      const queryNote = campaign.querySelector('[data-lrc-event-query-note]');
      if (!eventCards.length || !eventSelect) return;

      campaign.dataset.registrationCampaignReady = 'true';

      const normalize = (value = '') =>
        value
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

      const findEvent = (value) => {
        const slug = normalize(value);
        return eventCards.find((card) => normalize(card.dataset.eventSlug) === slug);
      };

      const updateCampaign = (eventCard, updateUrl = false) => {
        if (!eventCard) return;

        const slug = normalize(eventCard.dataset.eventSlug);
        const radio = eventCard.querySelector('[data-lrc-registration-event-radio]');
        if (radio) radio.checked = true;
        eventSelect.value = slug;

        eventCards.forEach((card) => card.classList.toggle('is-selected', card === eventCard));

        campaign.querySelectorAll('[data-lrc-event-bind]').forEach((binding) => {
          const key = binding.dataset.lrcEventBind;
          const value = eventCard.dataset[`event${key.charAt(0).toUpperCase()}${key.slice(1)}`];
          if (value) binding.textContent = value;
        });

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('city', slug);
          window.history.replaceState({}, '', url);
          if (queryNote) queryNote.hidden = true;
        }
      };

      eventSelect.addEventListener('change', () => updateCampaign(findEvent(eventSelect.value), true));

      eventCards.forEach((eventCard) => {
        eventCard.addEventListener('change', (event) => {
          if (event.target.matches('[data-lrc-registration-event-radio]')) updateCampaign(eventCard, true);
        });
      });

      const requestedCity = new URLSearchParams(window.location.search).get('city');
      const requestedEvent = requestedCity ? findEvent(requestedCity) : null;
      const checkedEvent = eventCards.find((card) => card.querySelector('[data-lrc-registration-event-radio]:checked'));
      const activeEvent = requestedEvent || checkedEvent || eventCards[0];

      if (requestedCity && !requestedEvent && queryNote) {
        queryNote.textContent = `The ${requestedCity} event is not currently listed. Showing ${activeEvent.dataset.eventCity} instead.`;
        queryNote.hidden = false;
      }

      updateCampaign(activeEvent, false);
    });
  }

  setupPolicyDialogs(scope = document) {
    scope.querySelectorAll('[data-lrc-policy-dialog]').forEach((dialog) => {
      if (dialog.dataset.policyDialogReady === 'true') return;
      dialog.dataset.policyDialogReady = 'true';

      const closeButton = dialog.querySelector('[data-lrc-policy-close]');
      const openButtons = [...document.querySelectorAll(`[data-lrc-policy-open][aria-controls="${CSS.escape(dialog.id)}"]`)];
      let returnFocus = null;

      const openDialog = (button) => {
        returnFocus = button;
        document.body.classList.add('lrc-policy-is-open');
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        closeButton?.focus({ preventScroll: true });
      };

      const closeDialog = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      };

      openButtons.forEach((button) => button.addEventListener('click', () => openDialog(button)));
      closeButton?.addEventListener('click', closeDialog);
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });
      dialog.addEventListener('close', () => {
        document.body.classList.remove('lrc-policy-is-open');
        returnFocus?.focus({ preventScroll: true });
      });
    });
  }

  setupPriorityMedia() {
    const heroImages = document.querySelectorAll('[data-lrc-priority-media] img');
    heroImages.forEach((image) => {
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.decoding = 'async';
    });

    const deferred = document.querySelectorAll('[data-lrc-preload-media]');
    const enqueue = (node) => {
      const src = node.currentSrc || node.src;
      if (!src || this.prefetched.has(src)) return;
      this.prefetched.add(src);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = 'low';
      document.head.appendChild(link);
    };

    const run = () => deferred.forEach(enqueue);
    window.requestIdleCallback ? requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 1200);
  }

  setupHeroVideos(scope = document) {
    scope.querySelectorAll('[data-lrc-responsive-hero-media]').forEach((media) => {
      if (media.dataset.responsiveHeroReady === 'true') return;
      media.dataset.responsiveHeroReady = 'true';

      const breakpoint = window.matchMedia('(max-width: 749px)');
      const variants = [...media.querySelectorAll('[data-lrc-hero-media-variant]')];

      const syncMedia = () => {
        const selected = breakpoint.matches ? 'mobile' : 'desktop';

        variants.forEach((variant) => {
          const active = variant.dataset.lrcHeroMediaVariant === selected;
          variant.hidden = !active;

          variant.querySelectorAll('.lrc-hero__native-video').forEach((video) => {
            video.preload = active ? 'auto' : 'none';
            if (!active) {
              video.pause();
              return;
            }

            video.muted = true;
            video.defaultMuted = true;
            video.loop = true;
            video.autoplay = true;
            video.playsInline = true;
            const playback = video.play();
            if (playback && typeof playback.catch === 'function') playback.catch(() => {});
          });

          variant.querySelectorAll('.lrc-hero__external-video').forEach((frame) => {
            const source = frame.dataset.lrcHeroVideoSrc;
            if (active && source && frame.src !== source) {
              frame.classList.remove('is-ready');
              frame.src = source;
            } else if (!active && frame.hasAttribute('src')) {
              frame.removeAttribute('src');
              frame.classList.remove('is-ready');
            }
          });
        });
      };

      syncMedia();
      breakpoint.addEventListener('change', syncMedia);
    });

    scope.querySelectorAll('.lrc-hero__native-video').forEach((video) => {
      if (video.closest('[data-lrc-hero-media-variant]')?.hidden) return;
      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
      video.removeAttribute('controls');

      const ensurePlayback = () => {
        const playback = video.play();
        if (playback && typeof playback.catch === 'function') {
          playback.catch(() => {});
        }
      };

      if (video.readyState >= 2) {
        ensurePlayback();
      } else {
        video.addEventListener('loadeddata', ensurePlayback, { once: true });
      }
    });

    scope.querySelectorAll('.lrc-hero__external-video').forEach((video) => {
      if (video.dataset.heroVideoReady === 'true') return;
      video.dataset.heroVideoReady = 'true';

      let revealTimer;
      const reveal = () => {
        window.clearTimeout(revealTimer);
        revealTimer = window.setTimeout(() => video.classList.add('is-ready'), 1800);
      };

      video.addEventListener('load', reveal, { once: true });
      revealTimer = window.setTimeout(() => video.classList.add('is-ready'), 3200);
    });
  }

  setupHeroScrollCards() {
    if (this.heroScrollCardsReady) return;
    this.heroScrollCardsReady = true;

    let animationFrame = null;
    const pageWrapper = document.querySelector('.page-wrapper');

    const update = () => {
      animationFrame = null;
      const viewportWidth = document.documentElement.clientWidth;
      const compact = viewportWidth < 750;
      const travel = compact ? 96 : 160;
      const maxInset = compact ? 10 : 24;
      const maxRadius = compact ? 22 : 38;
      const scrollTop = viewportWidth >= 990 && pageWrapper ? pageWrapper.scrollTop : window.scrollY;
      const rawProgress = Math.max(0, Math.min(scrollTop / travel, 1));
      const progress = reduceMotion.matches ? (rawProgress > 0 ? 1 : 0) : rawProgress;

      document.querySelectorAll('[data-lrc-scroll-card]').forEach((hero) => {
        hero.style.setProperty('--lrc-hero-inset', `${(maxInset * progress).toFixed(2)}px`);
        hero.style.setProperty('--lrc-hero-radius', `${(maxRadius * progress).toFixed(2)}px`);
        hero.style.setProperty('--lrc-hero-shadow-color', `rgba(47, 18, 92, ${(0.22 * progress).toFixed(3)})`);
        hero.classList.toggle('is-card', progress >= 0.98);
      });
    };

    const requestUpdate = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    pageWrapper?.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  }

  setupRegistrationHighlights(scope = document) {
    scope.querySelectorAll('[data-lrc-registration-highlight]').forEach((highlight) => {
      if (highlight.dataset.registrationHighlightReady === 'true') return;

      const slides = [...highlight.querySelectorAll('[data-lrc-registration-slide]')];
      const previousButton = highlight.querySelector('[data-lrc-registration-previous]');
      const nextButton = highlight.querySelector('[data-lrc-registration-next]');
      if (!slides.length) return;

      highlight.dataset.registrationHighlightReady = 'true';
      let activeIndex = 0;

      const show = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          slide.hidden = index !== activeIndex;
        });
      };

      previousButton?.addEventListener('click', () => show(activeIndex - 1));
      nextButton?.addEventListener('click', () => show(activeIndex + 1));
      show(0);
    });
  }

  setupEventGalleries(scope = document) {
    scope.querySelectorAll('[data-lrc-event-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;

      const dialogId = gallery.querySelector('[data-lrc-gallery-open]')?.getAttribute('aria-controls');
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (!dialog) return;

      const triggers = [...gallery.querySelectorAll('[data-lrc-gallery-open]')];
      const slides = [...dialog.querySelectorAll('[data-lrc-gallery-slide]')];
      const thumbnails = [...dialog.querySelectorAll('[data-lrc-gallery-thumbnail]')];
      const current = dialog.querySelector('[data-lrc-gallery-current]');
      const stage = dialog.querySelector('[data-lrc-gallery-stage]');
      const closeButton = dialog.querySelector('[data-lrc-gallery-close]');
      const previousButton = dialog.querySelector('[data-lrc-gallery-previous]');
      const nextButton = dialog.querySelector('[data-lrc-gallery-next]');
      if (!slides.length) return;

      gallery.dataset.galleryReady = 'true';
      let activeIndex = 0;
      let returnFocus = null;
      let touchStart = null;

      const showSlide = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;

        slides.forEach((slide, index) => {
          slide.hidden = index !== activeIndex;
        });

        thumbnails.forEach((thumbnail, index) => {
          thumbnail.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
        });

        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
        thumbnails[activeIndex]?.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      };

      const openGallery = (index, trigger) => {
        returnFocus = trigger;

        dialog.querySelectorAll('.lrc-gallery-modal__image').forEach((image, imageIndex) => {
          image.loading = 'eager';
          image.fetchPriority = imageIndex === index ? 'high' : 'auto';
        });

        showSlide(index);

        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }

        document.body.classList.add('lrc-gallery-is-open');
        closeButton?.focus({ preventScroll: true });
      };

      const closeGallery = () => {
        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
          document.body.classList.remove('lrc-gallery-is-open');
          returnFocus?.focus({ preventScroll: true });
        }
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          openGallery(Number(trigger.dataset.lrcGalleryOpen || 0), trigger);
        });
      });

      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          showSlide(Number(thumbnail.dataset.lrcGalleryThumbnail || 0));
        });
      });

      previousButton?.addEventListener('click', () => showSlide(activeIndex - 1));
      nextButton?.addEventListener('click', () => showSlide(activeIndex + 1));
      closeButton?.addEventListener('click', closeGallery);

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeGallery();
      });

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          showSlide(activeIndex - 1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          showSlide(activeIndex + 1);
        }

        if (event.key === 'Home') {
          event.preventDefault();
          showSlide(0);
        }

        if (event.key === 'End') {
          event.preventDefault();
          showSlide(slides.length - 1);
        }
      });

      stage?.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }, { passive: true });

      stage?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (!touch || !touchStart) return;

        const deltaX = touch.clientX - touchStart.x;
        const deltaY = touch.clientY - touchStart.y;
        touchStart = null;

        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
        showSlide(activeIndex + (deltaX < 0 ? 1 : -1));
      }, { passive: true });

      dialog.addEventListener('close', () => {
        document.body.classList.remove('lrc-gallery-is-open');
        returnFocus?.focus({ preventScroll: true });
      });
    });
  }

  setupGalleryDirectories(scope = document) {
    scope.querySelectorAll('[data-lrc-gallery-directory]').forEach((directory) => {
      if (directory.dataset.galleryDirectoryReady === 'true') return;

      const buttons = [...directory.querySelectorAll('[data-lrc-gallery-filter]')];
      const items = [...directory.querySelectorAll('[data-lrc-gallery-item]')];
      if (!buttons.length || !items.length) return;

      directory.dataset.galleryDirectoryReady = 'true';

      const show = (filter) => {
        buttons.forEach((button) => {
          const active = button.dataset.lrcGalleryFilter === filter;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        items.forEach((item) => {
          const active = filter === 'all' || item.dataset.lrcGalleryItem === filter;
          item.hidden = !active;
        });
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => show(button.dataset.lrcGalleryFilter || 'all'));
      });

      show('all');
    });
  }

  setupCityEventTabs(scope = document) {
    scope.querySelectorAll('[data-lrc-city-events]').forEach((section) => {
      if (section.dataset.cityEventsReady === 'true') return;

      const buttons = [...section.querySelectorAll('[data-lrc-city-tab]')];
      const panels = [...section.querySelectorAll('[data-lrc-city-panel]')];
      const select = section.querySelector('[data-lrc-city-select]');
      if (!buttons.length || !panels.length) return;

      section.dataset.cityEventsReady = 'true';

      const show = (city) => {
        buttons.forEach((button) => {
          const active = button.dataset.lrcCityTab === city;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          const active = panel.dataset.lrcCityPanel === city;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });

        if (select && [...select.options].some((option) => option.value === city)) {
          select.value = city;
        }
      };

      select?.addEventListener('change', () => show(select.value));

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => show(button.dataset.lrcCityTab || ''));

        button.addEventListener('keydown', (event) => {
          let nextIndex = null;

          if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;

          if (nextIndex === null) return;

          event.preventDefault();
          buttons[nextIndex].focus();
          show(buttons[nextIndex].dataset.lrcCityTab || '');
        });
      });

      show(buttons.find((button) => button.classList.contains('is-active'))?.dataset.lrcCityTab || buttons[0].dataset.lrcCityTab || '');
    });
  }

  setupFaqToggles(scope = document) {
    scope.querySelectorAll('[data-lrc-faq-group]').forEach((group) => {
      const toggle = group.parentElement?.querySelector('[data-lrc-faq-toggle]');
      const extras = [...group.querySelectorAll('[data-lrc-faq-extra]')];
      if (!toggle || !extras.length || toggle.dataset.faqReady === 'true') return;

      toggle.dataset.faqReady = 'true';

      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !expanded;

        extras.forEach((item) => {
          item.hidden = expanded;
        });

        toggle.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
        toggle.querySelector('.lrc-proof-faq-more__label').textContent = nextExpanded ? 'View less' : 'View more';

        if (expanded) {
          group.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest' });
        }
      });
    });
  }

  setupCommunityDialogs(scope = document) {
    scope.querySelectorAll('[data-lrc-community-dialog]').forEach((dialog) => {
      if (dialog.dataset.communityDialogReady === 'true') return;
      dialog.dataset.communityDialogReady = 'true';

      const closeButton = dialog.querySelector('[data-lrc-community-close]');
      const openButtons = [...document.querySelectorAll(`[data-lrc-community-open][aria-controls="${CSS.escape(dialog.id)}"]`)];
      const form = dialog.querySelector('[data-lrc-community-form]');
      const message = dialog.querySelector('[data-lrc-community-message]');
      const submitButton = dialog.querySelector('[data-lrc-community-submit]');
      let returnFocus = null;

      const openDialog = (button) => {
        returnFocus = button;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('lrc-community-is-open');
        dialog.querySelector('input, textarea, select')?.focus({ preventScroll: true });
      };

      const closeDialog = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else {
          dialog.removeAttribute('open');
          document.body.classList.remove('lrc-community-is-open');
          returnFocus?.focus({ preventScroll: true });
        }
      };

      openButtons.forEach((button) => button.addEventListener('click', () => openDialog(button)));
      closeButton?.addEventListener('click', closeDialog);

      form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        const endpoint = form.dataset.endpoint;
        if (!endpoint || !message || !submitButton) return;

        const fields = new FormData(form);
        const payload = {
          name: fields.get('name'),
          phone: fields.get('phone'),
          consent: fields.get('consent') === 'true',
          consentVersion: fields.get('consentVersion'),
          consentText: fields.get('consentText'),
          website: fields.get('website'),
          sourceUrl: window.location.href,
          shopDomain: window.location.hostname
        };

        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        message.hidden = false;
        message.dataset.state = 'loading';
        message.setAttribute('role', 'status');
        message.textContent = 'Joining the community…';

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'content-type': 'application/json', accept: 'application/json' },
            body: JSON.stringify(payload)
          });

          if (!response.ok) throw new Error('Community signup was not accepted');

          form.reset();
          message.dataset.state = 'success';
          message.textContent = form.dataset.successMessage || 'Thanks. You are on the list.';
        } catch {
          message.dataset.state = 'error';
          message.setAttribute('role', 'alert');
          message.textContent = 'We could not save your details. Please check your number and try again.';
        } finally {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });

      dialog.addEventListener('close', () => {
        document.body.classList.remove('lrc-community-is-open');
        returnFocus?.focus({ preventScroll: true });
      });
    });
  }

  setupProductGalleries() {
    document.querySelectorAll('[data-lrc-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.lrcGalleryReady === 'true') return;
      gallery.dataset.lrcGalleryReady = 'true';
      const image = gallery.querySelector('.lrc-product-gallery__image');
      const thumbs = [...gallery.querySelectorAll('[data-lrc-gallery-thumb]')];
      const previous = gallery.querySelector('[data-lrc-gallery-previous]');
      const next = gallery.querySelector('[data-lrc-gallery-next]');
      const counter = gallery.querySelector('[data-lrc-gallery-index]');
      if (!image || !thumbs.length) return;

      const showImage = (requestedIndex, { focus = false } = {}) => {
        const index = (requestedIndex + thumbs.length) % thumbs.length;
        const thumb = thumbs[index];
        const full = thumb?.dataset.full;
        if (!thumb || !full) return;

        image.removeAttribute('srcset');
        image.setAttribute('data-gallery-loading', 'true');
        image.src = full;
        image.alt = thumb.dataset.alt || image.alt;
        image.addEventListener('load', () => image.removeAttribute('data-gallery-loading'), { once: true });

        thumbs.forEach((item) => {
          item.classList.toggle('is-active', item === thumb);
          item.setAttribute('aria-current', item === thumb ? 'true' : 'false');
        });
        thumb.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        if (counter) counter.textContent = String(index + 1);
        if (focus) thumb.focus({ preventScroll: true });
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => showImage(index, { focus: true }));
        thumb.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') { event.preventDefault(); showImage(index + 1, { focus: true }); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); showImage(index - 1, { focus: true }); }
        });
      });

      const activeIndex = () => Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      previous?.addEventListener('click', () => showImage(activeIndex() - 1, { focus: true }));
      next?.addEventListener('click', () => showImage(activeIndex() + 1, { focus: true }));

      if (thumbs.length < 2) {
        previous?.setAttribute('hidden', '');
        next?.setAttribute('hidden', '');
        counter?.setAttribute('hidden', '');
        return;
      }

      let touchStartX = null;
      gallery.addEventListener('touchstart', (event) => { touchStartX = event.touches[0]?.clientX ?? null; }, { passive: true });
      gallery.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const delta = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
        touchStartX = null;
        if (Math.abs(delta) < 40) return;
        showImage(activeIndex() + (delta < 0 ? 1 : -1));
      }, { passive: true });
    });
  }

  setupProductForms() {
    document.querySelectorAll('[data-lrc-product-form]').forEach((form) => {
      const variantInput = form.querySelector('[data-lrc-variant-id]');
      const variantsNode = form.querySelector('[data-lrc-product-variants]');
      const section = form.closest('.lrc-product-main');
      const priceNode = section?.querySelector('[data-lrc-product-price]');
      const comparePriceNode = section?.querySelector('[data-lrc-product-compare-price]');
      const availabilityNode = section?.querySelector('[data-lrc-product-availability]');
      const stickyPriceNode = section?.querySelector('[data-lrc-product-sticky-price]');
      const addButton = form.querySelector('[data-lrc-add-button], [ref="addToCartButton"]');
      const stickyAddButton = section?.querySelector('[data-lrc-sticky-add-button]');
      const quantityInput = form.querySelector('[data-lrc-quantity-input]');
      const minus = form.querySelector('[data-lrc-quantity-minus]');
      const plus = form.querySelector('[data-lrc-quantity-plus]');
      let variants = [];

      try {
        variants = JSON.parse(variantsNode?.textContent || '[]');
      } catch (error) {
        variants = [];
      }

      const selectedOptions = () =>
        [...form.querySelectorAll('[data-lrc-product-option]')].map((group) => {
          const checked = group.querySelector('[data-lrc-option-input]:checked');
          return checked?.value || '';
        });

      const findVariant = () => {
        const options = selectedOptions();
        return variants.find((variant) => variant.options.every((option, index) => option === options[index]));
      };

      const syncVariant = () => {
        const variant = findVariant();
        if (!variant || !variantInput) return;

        variantInput.value = variant.id;
        if (priceNode && variant.price) priceNode.textContent = variant.price;
        if (stickyPriceNode && variant.price) stickyPriceNode.textContent = variant.price;
        if (comparePriceNode) {
          comparePriceNode.textContent = variant.compareAtPrice || '';
          comparePriceNode.classList.toggle('is-hidden', !variant.compareAtPrice);
        }
        if (availabilityNode) {
          availabilityNode.classList.toggle('is-sold-out', !variant.available);
          availabilityNode.lastChild.textContent = variant.available ? 'Available' : 'Sold out';
        }
        if (addButton) {
          addButton.disabled = !variant.available;
          const text = addButton.querySelector('.add-to-cart-text__content span span');
          if (text) text.textContent = variant.available ? addButton.dataset.defaultText || 'Add to bag' : 'Sold out';
          else addButton.textContent = variant.available ? addButton.dataset.defaultText || 'Add to bag' : 'Sold out';
        }
        if (stickyAddButton) {
          stickyAddButton.disabled = !variant.available;
          stickyAddButton.textContent = variant.available ? stickyAddButton.dataset.defaultText || 'Add to bag' : 'Sold out';
        }
      };

      if (addButton && !addButton.dataset.defaultText) {
        addButton.dataset.defaultText = addButton.querySelector('.add-to-cart-text__content')?.textContent.trim() || addButton.textContent.trim();
      }
      if (stickyAddButton && !stickyAddButton.dataset.defaultText) stickyAddButton.dataset.defaultText = stickyAddButton.textContent.trim();

      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-lrc-option-input]')) syncVariant();
      });

      // Keep the option cards reliably selectable on touch browsers. The
      // inputs are visually hidden, so explicitly mirror a card click onto
      // its associated radio before syncing the selected variant.
      form.querySelectorAll('.lrc-product-option__values label[for]').forEach((label) => {
        label.addEventListener('click', () => {
          const input = form.querySelector(`#${CSS.escape(label.htmlFor)}`);
          if (!input || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      // The sticky control lives outside the product form. Submit the same
      // form explicitly so it follows the exact add-to-cart/event path as the
      // primary PDP button and refreshes the custom header badge.
      stickyAddButton?.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(addButton || undefined);
        }
      });

      minus?.addEventListener('click', () => {
        const min = Number(quantityInput?.min || 1);
        const next = Math.max(min, Number(quantityInput?.value || min) - 1);
        if (quantityInput) quantityInput.value = String(next);
      });

      plus?.addEventListener('click', () => {
        const next = Number(quantityInput?.value || 1) + 1;
        if (quantityInput) quantityInput.value = String(next);
      });

      syncVariant();
    });
  }
}

let lrcTheme;
const boot = () => {
  lrcTheme = new LrcTheme();
  lrcTheme.init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  lrcTheme?.setupEventInclusions();
  lrcTheme?.setupHeroViewportHeight();
  lrcTheme?.setupHeroVideos(event.target);
  lrcTheme?.setupEventRegistrationLinks(event.target);
  lrcTheme?.setupRegistrationCampaigns(event.target);
  lrcTheme?.setupRegistrationHighlights(event.target);
  lrcTheme?.setupCityEventTabs(event.target);
  lrcTheme?.setupPolicyDialogs(event.target);
  lrcTheme?.setupEventGalleries(event.target);
  lrcTheme?.setupGalleryDirectories(event.target);
  lrcTheme?.setupCommunityDialogs(event.target);
});
